import React, { useEffect, useState } from 'react';
import {
  endDateError,
  requiredField,
  compactMenuProps,
  blockTypeOptions,
  timeBlockSuccess,
  PaperPropsWithoutScrollbar,
} from 'constants';
import {
  onSave,
  timeOptions,
  buildDateTime,
  refinePayload,
  timeValidation,
  useNotification,
  getDialogHeading,
  maxDateValidation,
  dropDownValidation,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { GridWrapper } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import { postV2TimeBlocks } from 'layouts/tutoringV2/services/manageTimeBlocksV2Api';

export const schema = yup.object().shape({
  blockType: dropDownValidation,

  subject: yup.string().when('blockType', {
    is: 'subject',
    then: (schema) => schema.required('Subject is required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  tutor: yup.string().when('blockType', {
    is: 'teacher',
    then: (schema) => schema.required('Tutor is required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  startDate: yup.date().required().typeError(requiredField),
  startTime: dropDownValidation,

  endDate: maxDateValidation('startDate', endDateError),
  endTime: timeValidation('End time must be after start time'),

  reason: yup.string().required(requiredField).nullable(),
});

const ManageTimeBlockDialog = ({ open, setOpen, fetchTimeBlocks }) => {
  const { setOpenNotification, setNotificationMessage, NotificationPopup } =
    useNotification();
  const dispatch = useDispatch();

  const [loadingButton, setLoadingButton] = useState(false);

  const handleClose = () => setOpen(false);

  const availableUsersList =
    useSelector((state) => state?.availableUsersList) || [];

  const subjects = useSelector((state) => state?.subjectsV2);
  const availableSubjectsData = subjects?.map((item) => ({
    id: item?.subjectId,
    value: item?.name,
  }));

  const availableUsersData =
    availableUsersList?.map((item) => ({
      id: item?.userId,
      value: `${item?.givenName} ${item?.familyName} (${item?.username})`,
    })) || [];

  const defaultValues = {
    subject: '',
    startDate: '',
    startTime: '08:00',
    endDate: '',
    endTime: '17:00',
    reason: '',
    tutor: '',
    blockType: blockTypeOptions?.[0]?.id || '',
  };

  const { control, reset, watch, handleSubmit, formState, trigger } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  const endDateValue = watch('endDate');
  const startDateValue = watch('startDate');
  const blockTypeValue = watch('blockType');

  useEffect(() => {
    if (startDateValue || endDateValue) {
      trigger('endTime');
    }
  }, [startDateValue, endDateValue, trigger]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open]);

  const onSubmit = (data) => {
    const {
      subject,
      tutor,
      blockType,
      startDate,
      endDate,
      startTime,
      endTime,
      reason,
    } = data;

    const startsAt = buildDateTime(startDate, startTime);
    const endsAt = buildDateTime(endDate, endTime);

    const payload = {
      ...(blockType === 'subject' && { subjectId: subject }),
      ...(blockType === 'teacher' && { teacherId: tutor }),
      startsAt,
      endsAt,
      reason,
    };

    const type = blockType === 'subject' ? 'subject' : 'teacher';
    console.log('payload', payload);
    onSave(
      timeBlockSuccess,
      true,
      (body) => postV2TimeBlocks(body, type),
      () => {},
      refinePayload(payload),
      '',
      fetchTimeBlocks,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton,
    );
  };

  return (
    <Dialog open={open} PaperProps={PaperPropsWithoutScrollbar}>
      {getDialogHeading('Time Block', handleClose)}

      <GridWrapper container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormInputDropdown
            name="blockType"
            label="Block Type"
            control={control}
            options={blockTypeOptions}
          />
        </Grid>

        {/* SUBJECT FIELD */}
        {blockTypeValue === 'subject' && (
          <Grid item xs={12} md={6}>
            <FormInputDropdown
              name="subject"
              label="Subject"
              control={control}
              errors={!!errors.subject}
              options={availableSubjectsData}
              helperText={errors?.subject?.message}
            />
          </Grid>
        )}

        {/* TUTOR FIELD */}
        {blockTypeValue === 'teacher' && (
          <Grid item xs={12} md={6}>
            <FormInputDropdown
              name="tutor"
              control={control}
              errors={!!errors.tutor}
              label="Select Teacher/Tutor"
              options={availableUsersData}
              helperText={errors?.tutor?.message}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <FormInputDate
            name="startDate"
            control={control}
            label="Start Date"
            maxDate={endDateValue}
            errors={!!errors.startDate}
            helperText={errors?.startDate?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormInputDropdown
            name="startTime"
            control={control}
            label="Start Time"
            options={timeOptions}
            errors={!!errors.startTime}
            menuProps={compactMenuProps}
            helperText={errors?.startTime?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormInputDate
            name="endDate"
            label="End Date"
            control={control}
            minDate={startDateValue}
            errors={!!errors.endDate}
            helperText={errors?.endDate?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormInputDropdown
            name="endTime"
            label="End Time"
            control={control}
            options={timeOptions}
            errors={!!errors.endTime}
            menuProps={compactMenuProps}
            helperText={errors?.endTime?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <FormInputText
            multiline
            minRows={5}
            maxRows={15}
            name="reason"
            label="Reason"
            control={control}
            errors={!!errors.reason}
            helperText={errors?.reason?.message}
          />
        </Grid>
      </GridWrapper>
      {renderSaveCancelButtons(
        handleClose,
        handleSubmit(onSubmit),
        loadingButton,
      )}
      <NotificationPopup />
    </Dialog>
  );
};

export default ManageTimeBlockDialog;
